import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import API from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Bank, Copy, ArrowSquareOut } from "@phosphor-icons/react";

const ACTIVE = ["pending_customer_approval", "pending_submission", "submitted", "active"];
const LABEL = { pending: "Set-up started, not finished", pending_customer_approval: "Awaiting client approval", pending_submission: "Active (being submitted)", submitted: "Active (submitted)",
  active: "Active", cancelled: "Cancelled", failed: "Failed", expired: "Expired", abandoned: "Set-up abandoned" };

/** Client page card: Direct Debit mandate status + set-up link (GoCardless). Hidden when GoCardless isn't connected. */
export default function ClientDirectDebitPanel({ clientId, clientName, canManage }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();

  const load = useCallback(async () => {
    try { const { data } = await API.get(`/integrations/gocardless/client/${clientId}`); setState(data); } catch { setState(null); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const dd = searchParams.get("dd");
    if (!dd) return;
    if (dd === "ok") toast.success("Direct Debit mandate set up");
    else if (dd === "incomplete") toast.warning("The Direct Debit set-up wasn't completed");
    else toast.error(searchParams.get("message") || "Direct Debit set-up failed");
    const next = new URLSearchParams(searchParams); next.delete("dd"); next.delete("message");
    setSearchParams(next, { replace: true });
    load();
  }, [searchParams, setSearchParams, load]);

  const start = async (openNow) => {
    setBusy(true);
    try {
      const { data } = await API.post(`/integrations/gocardless/mandate/${clientId}/start`, { back: `/clients/${clientId}` });
      setLink(data.url);
      if (openNow) window.open(data.url, "_blank", "noopener");
      else { try { await navigator.clipboard.writeText(data.url); toast.success("Set-up link copied — send it to the client"); } catch { window.prompt("Copy this link:", data.url); } }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create the set-up link"); }
    finally { setBusy(false); }
  };

  const cancel = async () => {
    if (!window.confirm(`Cancel ${clientName || "this client"}'s Direct Debit mandate? Future collections will fail until a new one is set up.`)) return;
    setBusy(true);
    try { await API.post(`/integrations/gocardless/mandate/${clientId}/cancel`); toast.success("Mandate cancelled"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Cancel failed"); }
    finally { setBusy(false); }
  };

  if (!state?.connected) return null;
  const m = state.mandate || {};
  const active = ACTIVE.includes(m.status);
  return (
    <div className="bg-card border border-border rounded-lg p-5 space-y-3" data-testid="client-direct-debit-panel">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold flex items-center gap-2"><Bank size={16} weight="duotone" className="text-indigo-600" /> Direct Debit (GoCardless)</h3>
        <Badge variant={active ? "default" : "secondary"} data-testid="client-dd-status">{LABEL[m.status] || (m.status ? m.status : "No mandate")}</Badge>
      </div>
      <p className="text-xs text-muted-foreground">
        {active ? `Mandate ${m.mandate_id} — contract invoices can be collected automatically.` : "Send the client a one-time set-up link; once they authorise it, invoices can be collected by Direct Debit."}
      </p>
      {canManage && (
        <div className="flex flex-wrap gap-2">
          {!active && <>
            <Button size="sm" onClick={() => start(false)} disabled={busy} className="gap-1" data-testid="client-dd-link-btn"><Copy size={13} /> {busy ? "Creating…" : "Copy set-up link for client"}</Button>
            <Button size="sm" variant="outline" onClick={() => start(true)} disabled={busy} className="gap-1" data-testid="client-dd-open-btn"><ArrowSquareOut size={13} /> Open set-up now</Button>
          </>}
          {active && <Button size="sm" variant="ghost" className="text-red-600" onClick={cancel} disabled={busy} data-testid="client-dd-cancel-btn">Cancel mandate</Button>}
        </div>
      )}
      {link && <code className="block text-[10px] break-all text-muted-foreground" data-testid="client-dd-link">{link}</code>}
    </div>
  );
}
